import { Point } from 'pixi.js';

import { Shared } from '../../App';
import { SplineDisplay } from '../../display/SplineDisplay';
import { Random } from '../../utils/Random';
import { Segment, SegmentPoint } from '../../utils/Segment';
import { CullingComponent } from '../components/CullingComponent';
import { Game } from '../Game';
import { BaseEntity } from './BaseEntity';
import { SpriteEntity } from './SpriteEntity';

export interface UndertrackEntityParams
{
    type: string,
    precision: number,
    thickness: number,
    baseline: number,
    x: number,
    y: number,
    img: string,
    maxPrecision: number,
    colinearAngle: number,
    verts: {x: number, y: number, cx: number, cy: number}[]
    props?: {z?: number}
}

export class UndertrackEntity extends BaseEntity
{
    public params!: UndertrackEntityParams;
    public spline?: SplineDisplay;
    public firstPoint!: SegmentPoint;
    public lastPoint!: SegmentPoint;
    public culling: CullingComponent;

    constructor()
    {
        super();
        this.culling = this.addComponent(CullingComponent);
    }

    public build(params: UndertrackEntityParams): void
    {
        this.params = params;
        const maxPrecision = params.maxPrecision || 16;
        const colinearAngle = params.colinearAngle || 0.01;
        const list = Segment.getPointsFromVerts(params.verts, false, maxPrecision, colinearAngle);
        const points = list.map((p) => new Point(p.x, p.y));

        this.updateSpline(params, points);

        this.x = params.x + ((this.spline?.width || 0) / 2);
        this.y = params.y + ((this.spline?.height || 0) / 2);
        this.z = params.props?.z ?? 0;

        const first = { ...list[0] };
        const last = { ...list[list.length - 1] };

        this.firstPoint = first.x < last.x ? first : last;
        this.lastPoint = first.x < last.x ? last : first;

        this.view.x = this.view.y = 0;
        this.culling.updateBounds();
    }

    private updateSpline(params: UndertrackEntityParams, points: Point[]): void
    {
        const splinePoints = points.slice(0);

        if (this.spline)
        {
            if (this.spline.parent) this.spline.parent.removeChild(this.spline);
            this.spline.destroy();
            this.spline = undefined;
        }

        if (!params.img) return;

        const splineTexture = Shared.assets.getTexture(params.img);

        this.spline = new SplineDisplay(splinePoints, splineTexture, {
            thickness: params.thickness || splineTexture.height,
            baseline: params.baseline,
            textureScale: 1,
            repeatDistance: 512,
        });
        this.spline.x = -this.spline.width / 2;
        this.spline.y = -this.spline.height / 2;
        this.view.addChild(this.spline);
    }
}

export class UndertrackFactory
{
    public static match(data: Record<string, any>): boolean
    {
        return data.type === 'SegmentEntity' && data.img === 'undertrack-tile';
    }

    public static create(game: Game, params: Record<string, any>): void
    {
        const ground = game.pool.get(UndertrackEntity) as UndertrackEntity;

        ground.build(params as UndertrackEntityParams);
        game.level.addEntity(ground);
        ground.z = -0.00002;

        const boulder1 = game.pool.get(SpriteEntity) as SpriteEntity;
        const x1 = ground.firstPoint.x + params.x;
        const y1 = ground.firstPoint.y + params.y;

        boulder1.build({
            x: x1, y: y1,
            r: 0,
            z: 0.000012,
            sx: 1,
            sy: 1,
            ax: 0.5,
            ay: 1,
            texture: 'tt-undertrack-boulder3',
        });
        game.level.addEntity(boulder1);

        const boulder2 = game.pool.get(SpriteEntity) as SpriteEntity;
        const x2 = ground.lastPoint.x + params.x;
        const y2 = ground.lastPoint.y + params.y;

        boulder2.build({
            x: x2, y: y2,
            r: 0,
            z: 0.000011,
            sx: 1,
            sy: 1,
            ax: 0.5,
            ay: 0,
            texture: 'tt-undertrack-boulder4',
        });
        game.level.addEntity(boulder2);

        this.decorate(game, ground);
    }

    private static decorate(game: Game, undetrack: UndertrackEntity): void
    {
        const points = Segment.getPointsFromVertsWithInterval(undetrack.params.verts, false, 500);

        // Remove start and end points
        points.shift();
        points.pop();

        for (const point of points)
        {
            const boulder = game.pool.get(SpriteEntity) as SpriteEntity;
            const dist = Random.range(20, 40);
            const r = point.a - (Math.PI * 0.5);
            const x = (undetrack.params.x + point.x) - (Math.sin(r) * dist);
            const y = (undetrack.params.y + point.y) + (Math.cos(r) * dist);
            const z = 0.000000009;
            const sx = Random.range(0.9, 1.1) * Random.pick(-1, 1);
            const sy = Random.range(0.9, 1.1);
            const variation = Math.round(Random.range(1, 3));

            boulder.build({
                x, y, z, r, sx, sy,
                ax: 0.5,
                ay: 0.5,
                texture: `tt-undertrack-boulder${variation}`,
            });
            game.level.addEntity(boulder);
        }
    }
}
